import type { TLotteryDrawEntry } from '@daymade/common-lib/dbJsonModels';
import { ZEntryLine } from '@daymade/common-lib/dbJsonModels';
import { z } from 'zod';
import {
  zodSelectDrawOrder,
  zodSelectTicket,
  zodSelectTicketEntry,
} from '../../generated/zodSchemas.generated';
import { ZPaymentRequestSelect, ZPurchasingAction } from '../../order/models';
import { ZSubscriptionSelect } from '../../subscription/models';
import { booleanParamSchema } from '../../utils';
import {
  ZDrawScheduleSelect,
  ZDrawSelect,
  ZDrawTypePlanSelect,
  ZDrawTypeSelect,
} from '../draw';

export const ZDrawOrderSelect = zodSelectDrawOrder
  .pick({
    id: true,
    status: true,
    orderId: true,
    playerId: true,
    firstDrawId: true,
    drawTypeCode: true,
    entries: true,
    price: true,
  })
  .extend({
    createdAt: z.coerce.date(),
    entries: z.array(ZEntryLine),
  });

export const ZTicketSelect = zodSelectTicket
  .pick({
    id: true,
    status: true,
    playerId: true,
    drawOrderId: true,
    drawId: true,
    drawTypePlanId: true,
    cost: true,
    paid: true,
  })
  .extend({
    createdAt: z.coerce.date(),
  });

export const ZTicketEntrySelect = zodSelectTicketEntry
  .pick({
    id: true,
    ticketId: true,
    entry: true,
  })
  .extend({
    createdAt: z.coerce.date(),
  });

export const ZTicketDetails = z.object({
  ticket: ZTicketSelect,
  entries: z.array(ZEntryLine),
  draw: ZDrawSelect,
  drawSchedule: ZDrawScheduleSelect,
  drawType: ZDrawTypeSelect,
  drawTypePlan: ZDrawTypePlanSelect,
  paymentRequest: ZPaymentRequestSelect,
  subscription: ZSubscriptionSelect.optional().nullable(),
});

export type TTicketDetails = z.infer<typeof ZTicketDetails>;

/** Represents a ticket that only contains, or has been filtered to contain only entry lines that are lottery lines */
export type TTicketDetailsOnlyLotteryDrawEntry = Omit<
  TTicketDetails,
  'entries'
> & {
  entries: TLotteryDrawEntry[];
};

export const ZAwardFreeEntriesRequest = z.object({
  firstName: z.string().min(1).max(100).optional(),
  lastName: z.string().min(1).max(100).optional(),
  playerEmail: z.string().email(),
  type: ZPurchasingAction, // schema.giveawayTypeEnum
  drawTypeCode: z.string(),
  drawScheduleId: z.coerce.number(),
  drawScheduleCode: z.string(),
  drawTypePlanCode: z.string(),
  drawTypePlanSubcode: z.string(),
  numOfEntries: z.coerce.number(),
  sendEmail: booleanParamSchema,
});

export type TAwardFreeEntriesRequest = z.infer<typeof ZAwardFreeEntriesRequest>;
